import * as apiClient from "@/api/client";

/**
 * Tax configuration interface
 */
export interface TaxConfig {
  [key: string]: {
    coef: number;
    type: string;
    label: string;
    color: string;
    text: string;
  };
}

/**
 * Singleton service for lazy loading tax configuration from backend
 */
class TaxService {
  private taxes: TaxConfig | null = null;
  private loadingPromise: Promise<TaxConfig> | null = null;
  private defaultTaxes: TaxConfig;

  constructor() {
    // Default taxes as fallback (current hardcoded values)
    this.defaultTaxes = {
      'igic_7': {coef: 0.07, type: 'igic', label: '7%', color: "blue", text: "IGIC 7%"},
      'igic_1': {coef: 0.01, type: 'igic', label: '1%', color: "blue", text: "IGIC 1%"},
      'igic_5': {coef: 0.05, type: 'igic', label: '5%', color: "blue", text: "IGIC 5%"},
      'igic_3': {coef: 0.03, type: 'igic', label: '3%', color: "blue", text: "IGIC 3%"},
      'igic_ne_0': {coef: 0., type: 'igic', label: '0%', color: "blue", text: "IGIC 0%"},
      'igic_ns_0': {coef: 0., type: 'igic', label: '0% NS', color: "blue", text: "IGIC 0% NS"},
      'igic_0': {coef: 0., type: 'igic', label: '0% Ex.', color: "blue", text: "IGIC 0% Ex."},
      'igic_95': {coef: 0.095, type: 'igic', label: '9.5%', color: "blue", text: "IGIC 9.5%"},
      'igic_15': {coef: 0.15, type: 'igic', label: '15%', color: "blue", text: "IGIC 15%"},
      'igic_20': {coef: 0.2, type: 'igic', label: '20%', color: "blue", text: "IGIC 20%"},
      'vat_21': {coef: 0.21, type: 'vat', label: '21%', color: "blue", text: "IVA 21%"},
      //'vat_21_bi': {coef: 0.21, type: 'vat', label: '21% Bienes Inversión', color: "blue", text: "IVA 21% Bienes Inversión"},
      'vat_12': {coef: 0.12, type: 'vat', label: '12%', color: "blue", text: "IVA 12%"},
      'vat_10_5': {coef: 0.105, type: 'vat', label: '10.5%', color: "blue", text: "IVA 10.5%"},
      'vat_10': {coef: 0.1, type: 'vat', label: '10%', color: "green", text: "IVA 10%"},
      'vat_75': {coef: 0.075, type: 'vat', label: '7.5%', color: "green", text: "IVA 7.5%"},
      'vat_5': {coef: 0.05, type: 'vat', label: '5%', color: "yellow", text: "IVA 5%"},
      'vat_4': {coef: 0.04, type: 'vat', label: '4%', color: "yellow", text: "IVA 4%"},
      'vat_2': {coef: 0.02, type: 'vat', label: '2%', color: "yellow", text: "IVA 2%"},
      'vat_ne_0': {coef: 0.0, type: 'vat', label: '0%', color: "yellow", text: "IVA 0%"},
      'vat_ns_0': {coef: 0.0, type: 'vat', label: '0% NS', color: "yellow", text: "IVA 0% NS"},
      'vat_0': {coef: 0.0, type: 'vat', label: '0% Ex.', color: "yellow", text: "IVA 0% Ex."},
      'vat_0_ex_exterior': {coef: 0.0, type: 'vat', label: '0% Ex. Ext.', color: "yellow", text: "IVA 0% Ex. Exterior"},
      'vat_re_5': {coef: 0.005, type: 'vat', label: 'RE 0.5%', color: "blue", text: "RE 0.5%"},
      'vat_re_625': {coef: 0.0062, type: 'vat', label: 'RE 0.62%', color: "blue", text: "RE 0.62%"},
      'vat_re_4': {coef: 0.014, type: 'vat', label: 'RE 1.4%', color: "blue", text: "RE 1.4%"},
      'vat_re_2': {coef: 0.052, type: 'vat', label: 'RE 5.2%', color: "blue", text: "RE 5.2%"},
      'vat_re_1_75': {coef: 0.0175, type: 'vat', label: 'RE 1.75%', color: "blue", text: "RE 1.75%"},
      'vat_re_1': {coef: 0.01, type: 'vat', label: 'RE 1%', color: "blue", text: "RE 1%"},
      'vat_re_026': {coef: 0.0026, type: 'vat', label: 'RE 0.26%', color: "blue", text: "RE 0.26%"},
      'igi_9_5': {coef: 0.095, type: 'igi', label: '9.5%', color: "blue", text: "IGI 9.5%"},
      'igi_4_5': {coef: 0.045, type: 'igi', label: '4.5%', color: "blue", text: "IGI 4.5%"},
      'igi_2_5': {coef: 0.025, type: 'igi', label: '2.5%', color: "blue", text: "IGI 2.5%"},
      'igi_1': {coef: 0.01, type: 'igi', label: '1%', color: "blue", text: "IGI 1%"},
      'igi_0': {coef: 0, type: 'igi', label: '0%', color: "blue", text: "IGI 0%"},
      'igi_ex_0': {coef: 0, type: 'igi', label: '0% Ex.', color: "blue", text: "IGI 0% Ex."},
      'igic2019_6_5': {coef: 0.065, type: 'igic2019', label: '6.5%', color: "blue", text: "IGIC 6.5%"},
      'igic2019_3': {coef: 0.03, type: 'igic2019', label: '3%', color: "blue", text: "IGIC 3%"},
      'igic2019_13_5': {coef: 0.135, type: 'igic2019', label: '13.5%', color: "blue", text: "IGIC 13.5%"},
    };
  }

  /**
   * Get taxes configuration. Loads from backend on first call, then caches.
   * Returns default taxes immediately if backend call fails.
   */
  async getTaxes(): Promise<TaxConfig> {
    // Return cached taxes if already loaded
    if (this.taxes) {
      return this.taxes;
    }

    // If already loading, wait for the existing promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // The api client builds its dispatchers on login (setHeaders), so at app start
    // there is nothing to ask yet. Serve the defaults and let a later call retry.
    if (!this.isApiClientReady()) {
      return this.defaultTaxes;
    }

    // Start loading from backend
    this.loadingPromise = this.fetchTaxesFromBackend();

    try {
      this.taxes = await this.loadingPromise;
      return this.taxes;
    } catch (error) {
      console.error('Failed to load taxes from backend, using defaults:', error);
      // Use default taxes as fallback
      this.taxes = this.defaultTaxes;
      return this.taxes;
    } finally {
      this.loadingPromise = null;
    }
  }

  /**
   * Tell whether the api client already has its query dispatcher built.
   */
  private isApiClientReady(): boolean {
    return Boolean(apiClient.query_dispatcher);
  }

  /**
   * Fetch taxes from backend using the query dispatcher of the api client.
   * The dispatcher is read here, not at import time, because the api client
   * replaces it when the session headers are set.
   */
  private async fetchTaxesFromBackend(): Promise<TaxConfig> {
    try {
      const response = await apiClient.query_dispatcher.dispatch('get_imposts', {});

      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response from backend');
      }

      return response as TaxConfig;
    } catch (error) {
      console.error('Error fetching taxes from backend:', error);
      throw error;
    }
  }

  /**
   * Get synchronous taxes (returns null if not loaded yet)
   * Use this only if you're sure taxes are already loaded
   */
  getTaxesSync(): TaxConfig | null {
    return this.taxes;
  }

  /**
   * Check if taxes are loaded
   */
  isLoaded(): boolean {
    return this.taxes !== null;
  }

  /**
   * Force reload taxes from backend
   */
  async reload(): Promise<TaxConfig> {
    this.taxes = null;
    this.loadingPromise = null;
    return this.getTaxes();
  }

  /**
   * Get default taxes (useful for SSR or initial render)
   */
  getDefaults(): TaxConfig {
    return this.defaultTaxes;
  }
}

// Export singleton instance
export const taxService = new TaxService();
